import { api, ApiError } from './api-client';
import { toast } from 'sonner';
import type {
  AutocaptureSettings,
  EngagementMode,
} from '@logspot-platform/platform-types';
import type { Project, DomainWhitelist } from '@/types';

type CreateProjectProps = {
  name: string;
  domainWhitelist: string[];
  silent?: boolean;
};

type UpdateProjectProps = {
  projectId: string;
  params: {
    name?: string;
    slug?: string;
    domainWhitelist?: DomainWhitelist;
    // Revenue analytics settings (Settings → Revenue).
    baseCurrency?: string;
    countPaymentsWithoutCurrency?: boolean;
    // Drop known bot/crawler traffic at ingestion.
    botFilteringEnabled?: boolean;
    // Trust the visitor IP the project's own proxy forwards.
    firstPartyProxyEnabled?: boolean;
    // URL query-parameter stripping (ingestion-level privacy).
    stripQueryParams?: boolean;
    queryParamAllowlist?: string[];
    // No-code autocapture (per-project master switch + versioned config).
    autocaptureEnabled?: boolean;
    autocaptureSettings?: AutocaptureSettings;
    engagementTracking?: EngagementMode;
  };
  silent?: boolean;
};

export const ProjectsService = {
  getProject: async ({ projectId }: { projectId: string }) => {
    const { data: project } = await api.get<{ data: Project }>(`/projects/${projectId}`);
    return project;
  },

  getProjects: async () => {
    const { data: { projects } } = await api.get<{ data: { projects: Project[] } }>(`/projects`);
    return projects;
  },

  createProject: async ({
    name,
    domainWhitelist,
    silent,
  }: CreateProjectProps) => {
    try {
      const { data } = await api.post<{ data: Project }>(`/projects`, {
        name,
        domainWhitelist,
      });
      if (!silent) {
        toast.success('Project Created');
      }
      return data;
    } catch (err) {
      // Plan-level domain limit hits 403 with a meaningful message; surface
      // it directly. Anything else stays generic-toasted.
      if (err instanceof ApiError && err.status === 403) {
        toast.error('Cannot Create Project', { description: err.message });
      } else {
        toast.error('We Encountered An Error', {
          description: 'Please try again.',
        });
      }
      console.error(err);
    }
  },

  updateProject: async ({ projectId, params, silent }: UpdateProjectProps) => {
    try {
      await api.put(`/projects/${projectId}`, params);
      if (!silent) {
        toast.success('Updated');
      }
    } catch (err) {
      // Re-throw conflicts so callers can show field-level validation
      // (e.g. slug already in use); other failures stay generic-toasted here.
      if (err instanceof ApiError && err.status === 409) {
        throw err;
      }
      toast.error('Could Not Update Project', { description: 'Please try again.' });
      console.error(err);
    }
  },

  deleteProject: async ({ projectId }: { projectId: string }) => {
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success('Project Deleted');
    } catch (err) {
      toast.error('Could Not Delete Project', { description: 'Please try again.' });
      console.error(err);
    }
  },
};
